import { sortCallback } from '@anecdotes/utils';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { firstValueFrom, of } from 'rxjs';
import { delay, filter, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  FieldsState,
  FieldState,
  DataManipulationConfig,
  FilterFieldDefinition,
  FilterOptionDefinition,
  OptionState,
  RelatedDataInfoState,
  QueryParams,
  SortDirectionType
} from '../../models';
import { FilterDefinitionStoreService } from '../../services';
import { DataFilterAction } from '../actions';
import { DataManipulationSelectors } from '../selectors';
import {
  createFilterIndex,
  createOptionState,
  FieldIndex,
  FieldsIndex,
  FilterIndex,
  FilterInstanceHelper,
  handleOptionWithCustomMatcher,
  OptionIndex,
  SearchHelper,
  treatUndefinedAsTrue,
} from '../utils';
import { isFilterFieldGroup } from '../../utils';
import { EvidenceTypeEnum, MERGED_COLUMN_SEPARATOR } from '@anecdotes/api-contracts';

interface OptionsHandlingContext {
  currentFieldState: FieldState;
  filterDefinitionItem: FilterFieldDefinition<any>;
  fieldIndex: FieldIndex;
  dataDictionary: Dictionary<any>;
}

@Injectable()
export class FilterEffects {
  private orderedHandlers: ((optionsHandlingContext: OptionsHandlingContext) => OptionState[])[] = [
    this.handleOptions,
    this.handleFixedOptions,
    this.handleOptionsWithCustomMatcher,
  ].map((handler) => handler.bind(this));

  constructor(
    private actions: Actions,
    private filterDefinitionStoreService: FilterDefinitionStoreService,
    private store: Store,
    private router: Router,
    private filterInstanceHelper: FilterInstanceHelper
  ) {}

  setData$ = createEffect(
    () => {
      return this.actions.pipe(
        ofType(DataFilterAction.setDataForFiltering),
        switchMap((action) => {
          return this.filterDefinitionStoreService.get(action.instanceId).pipe(
            filter((filterDef) => !!filterDef),
            take(1),
            switchMap(async (filterConfig) => {
              let dataDictionary: Dictionary<any>;

              if (action.data?.length) {
                dataDictionary = action.data.reduce(
                  (result, current) => {
                    const id = filterConfig.idSelector ? filterConfig.idSelector(current) : current[filterConfig.idPropertyName];
                    return {
                      ...result,
                      [id]: current,
                    };
                  },
                  {}
                );
              } else {
                dataDictionary = {};
              }

              const definitionState = this.buildDefinition(dataDictionary, filterConfig);
              const searchableData = SearchHelper.Instance.buildSearchableData(dataDictionary, filterConfig);

              let currentSortOptionId = await firstValueFrom(this.store
                .select(DataManipulationSelectors.Sort.createSortOptionSelector(filterConfig.filterInstanceId)));
              let sortDirection: SortDirectionType;

              if (!filterConfig.queryParamsDisabled) {
                sortDirection = this.getPredefinedSortDirectionValue();
                currentSortOptionId = currentSortOptionId || this.getPredefinedSortedByValue();
              }

              const sortedDataInfo = await this.sortDataAsync(dataDictionary, filterConfig, currentSortOptionId);
              sortDirection ??= filterConfig?.sortDefinition?.find((sortDefinition) => sortDefinition?.id === sortedDataInfo?.sortOptionId)?.directionStartPriority;

              this.store.dispatch(
                DataFilterAction.definitionStateBuilt({
                  instanceId: action.instanceId,
                  definitionState,
                  dataDictionary,
                  sortOptionId: sortedDataInfo.sortOptionId,
                  sortDirection,
                  orderedDataIds: sortedDataInfo.sortedIds,
                  searchableData,
                  searchedValue: action.shouldSetPredefinedSearchValue ? this.getPredefinedSearchValue() : null,
                })
              );

              return filterConfig;
            }),
            withLatestFrom(
              this.store.select(DataManipulationSelectors.Filter.CreateIsFilterInstanceInitializedSelector(action.instanceId))
            ),
            switchMap(([filterDef, isFilterInstanceInitialized]) => {
              if (!isFilterInstanceInitialized) {
                return of({});
              }

              return of({}).pipe(
                delay(filterDef.forceRecalculationTimeOut || 0),
                tap(() => {
                  this.store.dispatch(
                    DataFilterAction.forceCalculation({ instanceId: action.instanceId })
                  );
                })
              );
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  changeSortOption$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(DataFilterAction.changeSortOption),
        switchMap(async (action) => {
          const waitForDataInitialization$ = this.store
            .select(DataManipulationSelectors.Filter.CreateIsFilterInstanceInitializedSelector(action.instanceId))
            .pipe(filter(Boolean));

          await firstValueFrom(waitForDataInitialization$);

          const filterConfig = await firstValueFrom(this.filterDefinitionStoreService
            .get(action.instanceId)
            .pipe(filter((filterConfig) => !!filterConfig)));

          const dataToFilter = await firstValueFrom(
            this.store.select(DataManipulationSelectors.Filter.createDataToFilterSelector(action.instanceId))
          );

          const result = await this.sortDataAsync(dataToFilter, filterConfig, action.sortOptionId);

          this.store.dispatch(
            DataFilterAction.sortingFinished({
              instanceId: action.instanceId,
              sortOptionId: result.sortOptionId,
              sortedIds: result.sortedIds,
              sortDirection: action.sortDirection,
            })
          );
        })
      ),
    { dispatch: false }
  );

  queryParams$ = createEffect(
    () =>
      this.store.pipe(DataManipulationSelectors.Aggregation.selectFilterRelatedQueryParamsObject).pipe(
        tap((queryParamsFromFilter) => {
          const modifiedQueryParams = { ...this.router.routerState.root.snapshot.queryParams };
          let isModified = false;

          Object.keys(queryParamsFromFilter).forEach((fieldId) => {
            const optionValues = Array.from(queryParamsFromFilter[fieldId]);

            if (optionValues.length) {
              const newOptionsString = Array.from(queryParamsFromFilter[fieldId])
                .join(',');

              if (modifiedQueryParams[fieldId] !== newOptionsString) {
                isModified = true;
              }
              modifiedQueryParams[fieldId] = newOptionsString;
            } else if (fieldId in modifiedQueryParams) {
              delete modifiedQueryParams[fieldId];
              isModified = true;
            }
          });

          if (isModified) {
            this.router.navigate([], {
              queryParams: modifiedQueryParams,
              replaceUrl: true
            });
          }
        })
      ),
    { dispatch: false }
  );

  private async sortDataAsync(
    dataDictionary: Dictionary<any>,
    filterConfig: DataManipulationConfig<any>,
    sortOptionId: string
  ): Promise<{ sortedIds: string[]; sortOptionId: string }> {
    if (!filterConfig.sortDefinition?.length) {
      return {
        sortOptionId: sortOptionId,
        sortedIds: Object.keys(dataDictionary),
      };
    }

    const data = Object.values(dataDictionary);
    let currentSortDef = sortOptionId
      ? filterConfig.sortDefinition.find((sortDefinition) => sortDefinition.id === sortOptionId)
      : filterConfig.sortDefinition.find(
        (sortDefinition) => sortDefinition.chooseByDefault && sortDefinition.propertySelector,
      ) ?? filterConfig.sortDefinition.find((sortDefinition) => sortDefinition.propertySelector);

    if (!currentSortDef) {
      currentSortDef = filterConfig.sortDefinition[0];
    }

    const sorted = data.sort((first, second) => {
      if (!currentSortDef.propertySelector) {
        return 0;
      }

      const firstPropValue = currentSortDef.propertySelector(first);
      const secondPropValue = currentSortDef.propertySelector(second);

      const sortFunc = currentSortDef.sortFunc || sortCallback;
      return sortFunc(firstPropValue, secondPropValue);
    });

    const sortedIds = sorted.map((item) => {
      if (filterConfig.idSelector) {
        return filterConfig.idSelector(item);
      }

      return item[filterConfig.idPropertyName];
    });

    return {
      sortedIds,
      sortOptionId: currentSortDef.id,
    };
  }

  private getPredefinedSearchValue(): string {
    const queryParamValue = this.router.routerState.root.snapshot.queryParams[QueryParams.search];

    if (queryParamValue) {
      try {
        return decodeURIComponent(queryParamValue);
      } catch {
        return queryParamValue;
      }
    }
  }

  private getPredefinedSortedByValue(): string {
    return this.router.routerState.root.snapshot.queryParams[QueryParams.sortBy];
  }

  private getPredefinedSortDirectionValue(): SortDirectionType {
    return this.router.routerState.root.snapshot.queryParams[QueryParams.sortDirection];
  }

  private buildDefinition(dataDictionary: Dictionary<any>, filterConfig: DataManipulationConfig): FieldsState {
    const data = Object.values(dataDictionary);
    const filterIndex: FilterIndex = createFilterIndex(data, filterConfig);
    const fieldsIndex: FieldsIndex = filterIndex.fieldsIndex;

    const definition: FieldsState = {};

    filterConfig.fields.flatMap((fieldOrGroup) => isFilterFieldGroup(fieldOrGroup)).forEach((filterDefinitionItem) => {
      const fieldIndex: FieldIndex = fieldsIndex[filterDefinitionItem.fieldId];

      let relatedDataInfo: RelatedDataInfoState;

      if (fieldIndex) {
        relatedDataInfo = Object.keys(fieldIndex.allNestedDataMapping).reduce((result, dataId) => {
          result[dataId] = { nestedIds: Object.keys(fieldIndex.allNestedDataMapping[dataId].nestedData) };
          return result;
        }, {});
      }

      const fieldState: FieldState = {
        id: filterDefinitionItem.fieldId,
        isSingleSelection: filterDefinitionItem.singleSelection,
        translationKey: filterDefinitionItem.translationKey,
        useSort: filterDefinitionItem.useSort,
        multiSelector: filterDefinitionItem.multiSelector,
        displayed: treatUndefinedAsTrue(filterDefinitionItem.displayed),
        displayedInSelectedFiltersList: treatUndefinedAsTrue(filterDefinitionItem.displayedInSelectedFiltersList),
        hideInZeroCount: filterDefinitionItem.hideInZeroCount,
        isSwitcher: filterDefinitionItem.isSwitcher,
        ignoreForCounting: filterDefinitionItem.ignoreForCounting,
        ignoreForReset: filterDefinitionItem.ignoreForReset,
        expanded: filterDefinitionItem.expanded,
        maxVisibleOptions: filterDefinitionItem.maxVisibleOptions,
        options: {},
        shouldNestedBeCounted: !!filterDefinitionItem.nestedDataDefinition,
        nestedGroupId: filterDefinitionItem.nestedDataDefinition?.nestedGroupId,
        relatedDataInfo,
        notTrackedInQueryParams: !!filterDefinitionItem.notTrackedInQueryParams,
      };

      definition[filterDefinitionItem.fieldId] = fieldState;

      this.orderedHandlers.forEach((handler) => {
        handler({
          fieldIndex,
          filterDefinitionItem,
          dataDictionary,
          currentFieldState: fieldState,
        })?.forEach((currentOptionState) => {
          if (
            fieldState.id === 'plugins' &&
            currentOptionState?.id?.includes?.(MERGED_COLUMN_SEPARATOR) &&
            Object.values(dataDictionary).some(
              (evidence) => evidence.evidence_service_id === currentOptionState.id && evidence.evidence_type === EvidenceTypeEnum.MERGED
            )
          ) {
            return;
          }
          if (currentOptionState) {
            if (!currentOptionState.relatedDataInfo) {
              currentOptionState.relatedDataInfo = {};
            }
            currentOptionState.value ??= currentOptionState.id;

            fieldState.options[currentOptionState.id] = currentOptionState;
          }
          if (fieldState.id === 'plugins') {
            const allEvidence = Object.values(dataDictionary);
            const idToDisplayNameMap = Object.values(dataDictionary).reduce((acc, evidence) => {
              acc[evidence.evidence_service_id] = evidence.evidence_service_display_name;
              return acc;
            }, {} as Record<string, string>);
            for (const evidence of allEvidence) {
              const evidenceId = evidence.evidence_id;
              const serviceId = evidence.evidence_service_id;

              if (evidence.evidence_type !== EvidenceTypeEnum.MERGED) { 
                continue; 
              }

              if (serviceId.includes(MERGED_COLUMN_SEPARATOR)) {
                const uniqueParts = Array.from(new Set(serviceId.split(MERGED_COLUMN_SEPARATOR)));
                for (const part of uniqueParts as string[]) {
                  const displayName = idToDisplayNameMap[part];
                  const pluginOption = fieldState.options[displayName];
                  if (!pluginOption) { 
                    continue; 
                  }

                  pluginOption.relatedDataInfo ??= {};
                  if (!pluginOption.relatedDataInfo[evidenceId]) {
                    pluginOption.relatedDataInfo[evidenceId] = { nestedIds: [] };
                    pluginOption.hasRelatedData = true;
                  }
                }
              }
            }
          }
        });
      });
    });

    return this.filterInstanceHelper.applyOptionsFromQueryParams(this.router.routerState.root.snapshot.queryParams, definition);
  }

  /** Order matters. Handlers may reuse the result of previous handlers (handleOptionsWithCustomMatcher for an instance) */

  private handleFixedOptions(optionsHandlingContext: OptionsHandlingContext): OptionState[] {
    return optionsHandlingContext.filterDefinitionItem.fixedOptions?.map((filterOption) => {
      const optionIndex: OptionIndex = optionsHandlingContext.fieldIndex?.optionsIndex[filterOption.value];
      const optionState: OptionState = createOptionState(
        optionsHandlingContext.currentFieldState,
        filterOption,
        optionIndex
      );
      return this.toFixedOption(optionState);
    });
  }

  private handleOptions(optionsHandlingContext: OptionsHandlingContext): OptionState[] {
    const optionsToHandle = [];

    if (optionsHandlingContext.filterDefinitionItem.options) {
      optionsToHandle.push(...optionsHandlingContext.filterDefinitionItem.options);
    } else if (optionsHandlingContext.fieldIndex && optionsHandlingContext.filterDefinitionItem.propertySelector) {
      const valuesSet = new Set<string>(Object.keys(optionsHandlingContext.fieldIndex.optionsIndex));

      const distinctOptions = Array.from(valuesSet).map(
        (value) => ({ optionId: value, value: value } as FilterOptionDefinition<any>)
      );

      optionsToHandle.push(...distinctOptions);
    }

    const res = optionsToHandle.map((filterOption) => {
      const optionIndex: OptionIndex = optionsHandlingContext.fieldIndex?.optionsIndex[filterOption.value];

      if (optionIndex) {
        const optionState = createOptionState(optionsHandlingContext.currentFieldState, filterOption, optionIndex);

        if (optionState.hasRelatedData) {
          return optionState;
        }
      }
    });

    return res;
  }

  private handleOptionsWithCustomMatcher(optionsHandlingContext: OptionsHandlingContext): OptionState[] {
    const result: OptionState[][] = [];

    result.push(
      optionsHandlingContext.filterDefinitionItem.options
        ?.filter((filterOption) => filterOption.customMatcher)
        .map((filterOption) =>
          handleOptionWithCustomMatcher(
            optionsHandlingContext.fieldIndex,
            optionsHandlingContext.currentFieldState,
            filterOption,
            optionsHandlingContext.dataDictionary
          )
        )
    );

    result.push(
      optionsHandlingContext.filterDefinitionItem.fixedOptions
        ?.filter((filterOption) => filterOption.customMatcher)
        .map((filterOption) => {
          const optionState = handleOptionWithCustomMatcher(
            optionsHandlingContext.fieldIndex,
            optionsHandlingContext.currentFieldState,
            filterOption,
            optionsHandlingContext.dataDictionary
          );
          return optionState ? this.toFixedOption(optionState) : null;
        })
    );

    return result.filter((optionStates) => optionStates).flatMap((optionStates) => optionStates);
  }

  private toFixedOption(optionState: OptionState): OptionState {
    optionState.isFixed = true;
    return optionState;
  }
}
